import { inject, InjectionToken } from '@angular/core';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { ConversationConfig, RouteConfig } from '@galaxy/conversation/core';
import { FeatureFlagService, PartnerService, WhitelabelService } from '@galaxy/partner';
import { GlobalParticipantType, InboxApiService, PlatformLocation } from '@vendasta/conversation';
import { of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, shareReplay, switchMap, take } from 'rxjs/operators';
import { AppConfigService } from '../app-config.service';
import { DashboardService } from './../dashboard/dashboard.service';
import { GetConfigurationResponse } from '@vendasta/conversation/lib/_internal/objects';
import { openZendeskLauncher } from '../../lib/zendesk';

export const INBOX_CONFIG_FOR_CONVERSATION = new InjectionToken<ConversationConfig>(
  '[Partner Center Client]: Token for Conversation config',
  {
    providedIn: 'root',
    factory: function (): ConversationConfig {
      const userService = inject(DashboardService);
      const partnerService = inject(PartnerService);
      const whitelabelService = inject(WhitelabelService);
      const featureFlagService = inject(FeatureFlagService);
      const appConfigService = inject(AppConfigService);
      const inboxApiService = inject(InboxApiService);

      const userId$ = userService.getCurrentUser().pipe(
        map((user) => user?.userId),
        filter<string>(Boolean),
        distinctUntilChanged(),
        shareReplay({ refCount: true, bufferSize: 1 }),
      );

      const accountGroupId$ = of('');

      const partnerId$ = partnerService.getPartnerId().pipe(
        map((partnerId) => partnerId),
        filter<string>(Boolean),
        distinctUntilChanged(),
        shareReplay({ refCount: true, bufferSize: 1 }),
      );

      const country$ = partnerId$.pipe(
        take(1),
        switchMap((partnerId) => {
          return whitelabelService
            .getConfiguration(partnerId)
            .pipe(map((config) => config.mailingConfiguration.mailingCountry));
        }),
        distinctUntilChanged(),
        shareReplay({ refCount: true, bufferSize: 1 }),
      );

      const geographicalState$ = of('');

      const partnerBrandName$ = of('');

      const companyName$ = of('');

      const featureFlag$ = partnerId$.pipe(
        take(1),
        switchMap((partnerId) => {
          return featureFlagService.batchGetStatus(partnerId, 'default', ['inbox_tab_pcc']);
        }),
        map((featureFlags) => {
          return featureFlags['inbox_tab_pcc'];
        }),
        shareReplay({ refCount: true, bufferSize: 1 }),
      );

      const platformLocation = PlatformLocation.PLATFORM_LOCATION_PARTNER_CENTER;

      const routes$ = of({
        root: '',
        sendNewMessage: 'new-message',
        useModal: true,
        getAiAssistantChatPath: (aiAssistantId: string) => `/inbox/ai-assistant/${aiAssistantId}/chat`,
      } as RouteConfig);

      const marketId$ = of('');

      const isImpersonating$ = appConfigService.config$.pipe(map((config) => config.isImpersonated));

      const webChatEnabled$ = of(true);

      const inboxConfig$ = partnerId$.pipe(
        switchMap((accountGroupId) => {
          if (!accountGroupId) {
            return of({} as GetConfigurationResponse);
          }
          return inboxApiService
            .getConfiguration({
              subjectParticipant: {
                participantType: GlobalParticipantType.GLOBAL_PARTICIPANT_TYPE_PARTNER,
                internalParticipantId: accountGroupId,
              },
            })
            .pipe(catchError(() => of({} as GetConfigurationResponse)));
        }),
        map((config) => config?.configuration),
        shareReplay({ refCount: true, bufferSize: 1 }),
      );

      const smsAIResponderEnabled$ = inboxConfig$.pipe(
        map((config) => config?.aiConfiguration?.smsResponderEnabled || false),
      );

      const smsEnabled$ = of(true);
      const whatsAppEnabled$ = of(false);

      const openZendeskChat = () => {
        openZendeskLauncher();
      };

      const internalDomainLinks = ['partners.vendasta.com'];

      return {
        userId$,
        accountGroupId$,
        country$,
        geographicalState$,
        partnerId$,
        partnerBrandName$,
        companyName$,
        featureFlag$,
        platformLocation,
        routes$,
        marketId$,
        isImpersonating$,
        webChatEnabled$,
        smsEnabled$,
        smsAIResponderEnabled$,
        whatsAppEnabled$,
        openZendeskChat,
        internalDomainLinks,
      };
    },
  },
);
